<template>
    <ThDialog
        prompt="⭐ Larger stones require refined support. We’ve upgraded your design to the Grande version to ensure enduring beauty and security."
        :is-open="hasSwitchedToGrande"
        :cancel-label="null"
        confirm-label="OK"
        do-not-hide-body-overflow
        @close="confirm"
        @confirm="confirm"
    >
        <div
            v-if="pieces.length"
            class="confetti"
            aria-hidden="true"
        >
            <span
                v-for="piece in pieces"
                :key="piece.id"
                class="piece"
                :class="piece.shape"
                :style="piece.style"
            />
        </div>
    </ThDialog>
</template>

<script lang="ts">
type ConfettiPiece = {
    id: number
    shape: 'ribbon' | 'circle'
    style: Record<string, string>
}

const CONFETTI_COLORS = [
    '#e8453c', // red
    '#f9a825', // amber
    '#ffe14d', // yellow
    '#3cb44b', // green
    '#2196f3', // blue
    '#9c27b0', // purple
    '#ff6fae', // pink
]
const PIECE_COUNT = 70
const MAX_DELAY_MS = 1200
const MAX_DURATION_MS = 3200
</script>

<script lang="ts" setup>
import { onUnmounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { getTimeout, getWindow } from '@/helpers/globals'
import { useRingBuilderGranderResultStore } from '@/plugins/ring-builder-grande-result-store'

import ThDialog from '@/components/layout/modal/ThDialog.vue'

const { hasSwitchedToGrande } = storeToRefs(useRingBuilderGranderResultStore())
const confirm = () => {
    hasSwitchedToGrande.value = false
}

const pieces = ref<ConfettiPiece[]>([])
let cleanupId: number | undefined

const random = (min: number, max: number): number =>
    min + Math.random() * (max - min)

const prefersReducedMotion = (): boolean =>
    getWindow()?.matchMedia('(prefers-reduced-motion: reduce)').matches === true

const timeout = getTimeout()
const animate = () => {
    if (!timeout || prefersReducedMotion()) {
        return
    }

    const { clearTimeout, setTimeout } = timeout

    clearTimeout(cleanupId)

    pieces.value = Array.from(
        { length: PIECE_COUNT },
        (_, id): ConfettiPiece => ({
            id,
            shape: id % 3 === 0 ? 'circle' : 'ribbon',
            style: {
                '--left': `${random(0, 100)}%`,
                '--size': `${random(6, 12)}px`,
                '--drift': `${random(-140, 140)}px`,
                '--delay': `${random(0, MAX_DELAY_MS)}ms`,
                '--duration': `${random(2600, MAX_DURATION_MS)}ms`,
                '--color': CONFETTI_COLORS[id % CONFETTI_COLORS.length]!,
            },
        })
    )

    cleanupId = setTimeout(() => {
        pieces.value = []
    }, MAX_DELAY_MS + MAX_DURATION_MS)
}

watch(hasSwitchedToGrande, (val) => {
    if (val) {
        animate()
    }
})

if (timeout) {
    const { clearTimeout } = timeout

    onUnmounted(() => {
        clearTimeout(cleanupId)
    })
}
</script>

<style lang="scss" scoped>
.confetti {
    position: absolute;
    inset: 0;
    z-index: 3100;
    overflow: hidden;
    pointer-events: none;
}

.piece {
    position: absolute;
    top: -50px;
    left: var(--left);
    width: var(--size);
    height: var(--size);
    will-change: transform, opacity;
    animation: confetti-fall var(--duration) var(--delay)
        cubic-bezier(0.25, 0.6, 0.4, 1) forwards;

    &::before {
        content: '';
        display: block;
        width: 100%;
        height: 100%;
        background-color: var(--color);
        animation: confetti-spin calc(var(--duration) / 3) var(--delay) linear
            infinite;
    }

    &.ribbon::before {
        height: 180%;
    }

    &.circle::before {
        border-radius: 50%;
    }
}

@keyframes confetti-fall {
    0% {
        opacity: 0;
        transform: translate3d(0, 0, 0);
    }

    10% {
        opacity: 1;
    }

    85% {
        opacity: 1;
    }

    100% {
        opacity: 0;
        transform: translate3d(var(--drift), 350px, 0);
    }
}

@keyframes confetti-spin {
    from {
        transform: rotate3d(0.6, 1, 0.4, 0deg);
    }

    to {
        transform: rotate3d(0.6, 1, 0.4, 360deg);
    }
}
</style>
